import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Wallet, ScanLine, ShieldCheck, ChevronRight, Camera } from "lucide-react";
import { api } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { PageHeader, StatCard, EmptyState, StatusBadge, money, shortDate } from "../components/common";
import { fileUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

export default function CustomerWallet() {
  const { data: receipts = [], isLoading } = useQuery({
    queryKey: ["mine"],
    queryFn: async () => (await api.get("/receipts/mine")).data,
  });

  const total = receipts.reduce((s, r) => s + (r.total || 0), 0);
  const active = receipts.filter((r) => r.warranty_status === "active").length;

  return (
    <AppLayout>
      <PageHeader eyebrow="My wallet" title="Your receipts"
        subtitle="Every purchase you've claimed, with warranty tracking and proof on hand."
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/wallet/add"><Button className="bg-brand text-white hover:bg-brand-hover" data-testid="add-receipt-cta"><Camera className="mr-2 h-4 w-4" />Add receipt</Button></Link>
            <Link to="/claim"><Button variant="outline" data-testid="claim-cta"><ScanLine className="mr-2 h-4 w-4" />Claim via QR</Button></Link>
          </div>
        }
      />

      <div className="mb-8 grid gap-4 sm:grid-cols-3">
        <StatCard label="Receipts" value={receipts.length} icon={Wallet} />
        <StatCard label="Total tracked" value={money(total)} icon={Wallet} accent="bg-success/12 text-success" />
        <StatCard label="Active warranties" value={active} icon={ShieldCheck} accent="bg-brand-muted text-brand" />
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-44 w-full rounded-2xl" />)}</div>
      ) : receipts.length === 0 ? (
        <EmptyState icon={Wallet} title="Your wallet is empty" subtitle="Add a receipt from any store, or claim one from a partner retailer's QR."
          action={<Link to="/wallet/add"><Button className="bg-brand text-white hover:bg-brand-hover">Add a receipt</Button></Link>} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {receipts.map((r) => (
            <Link key={r.id} to={`/wallet/receipt/${r.id}`} data-testid={`wallet-card-${r.id}`}
              className="group flex flex-col rounded-2xl border border-border bg-card p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    {r.store_logo_path && <img src={fileUrl(r.store_logo_path)} alt="" className="h-6 w-6 rounded object-cover" />}
                    <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{r.store_name}</p>
                  </div>
                  <p className="mt-1 font-heading text-lg font-extrabold leading-tight">{r.items?.[0]?.name || "Purchase"}</p>
                  {r.items?.length > 1 && <p className="text-xs text-muted-foreground">+{r.items.length - 1} more item(s)</p>}
                </div>
                {(r.status === "self_added" || r.warranty_status) && (
                  <div className="flex flex-col items-end gap-1.5">
                    {r.status === "self_added" && <StatusBadge status="self_added" />}
                    {r.warranty_status && <StatusBadge status={r.warranty_status} />}
                  </div>
                )}
              </div>
              <div className="mt-auto flex items-end justify-between pt-6">
                <div>
                  <p className="text-xs text-muted-foreground">{shortDate(r.purchase_date)}</p>
                  <p className="font-heading text-2xl font-black tabular-nums">{money(r.total)}</p>
                </div>
                <span className="grid h-9 w-9 place-items-center rounded-full bg-secondary text-muted-foreground transition-colors group-hover:bg-brand group-hover:text-white">
                  <ChevronRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
